// public/js/features/devices/device-type.js
// Finer device "type" derived from existing fields (no DB migration needed):
//   category = Tracker (GPS) | BLE Sensor | BLE Beacon
//   model    = the Superb-branded product name (never the vendor model)
// Customer-facing labels resolve through branded-models.js, so the UI shows
// "Superb LocatePro" rather than "Oyster3". The raw vendor model (DM_MODELS)
// stays here for internal use only (never rendered to customers).

// Stage 2: DigitalMatter product-ID → friendly model name.
// Authoritative list from "DMT JSON Device Integration 1.29" (digitalmatter.com),
// plus our fleet-specific entries:
//   118 = GRIFFIN-AIR (own brand; not in the DM list; the vast majority 73/75)
//   92  = Oyster Edge (newer DM device; confirmed via linked asset "… Oyser Edge")
//   110 = Barra GPS   (per the DM product-ID list)
export const DM_MODELS = {
  // fleet-specific / newer than the doc
  118: 'GRIFFIN-AIR',
  92: 'Oyster Edge',
  // from the DM v1.29 product-ID list
  4: 'G50', 17: 'G52 Solar', 23: 'G60', 28: 'G100', 33: 'Remora', 34: 'Dart',
  52: 'Sting', 58: 'Oyster', 62: 'Remora 2', 67: 'G62 Cellular', 68: 'Dart2',
  70: 'G200', 72: 'Yabby Cellular Wi-Fi', 73: 'Yabby Cellular GPS', 74: 'Falcon',
  75: 'Bolt', 77: 'Oyster2', 78: 'Eagle', 79: 'G120', 87: 'Oyster3 4G',
  88: 'Bolt2', 89: 'G70 4G', 91: 'Dart3 4G', 93: 'G70 2G', 94: 'Dart3 2G',
  96: 'Remora3', 97: 'Yabby3', 100: 'Hawk', 102: 'Oyster3 4G BLE',
  106: 'Oyster Global', 107: 'Remora Global', 108: 'G150 Global', 110: 'Barra GPS',
};

import { brandedProductForDevice } from './branded-models.js';
import { deviceAssetUrl } from '../manual/manual-assets.js';

// BLE sub-types that are positioning beacons (vs. real sensor tags).
const BLE_BEACON_SUBTYPES = new Set(['iBeacon', 'Eddystone', 'Eddystone-TLM']);

export function deviceCategory(d) {
  if (!d) return 'Unknown';
  if (d.type === 'bluetooth') {
    return BLE_BEACON_SUBTYPES.has(d.subType) ? 'BLE Beacon' : 'BLE Sensor';
  }
  if (d.type === 'digitalmatter') return 'Tracker (GPS)';
  return d.type || 'Unknown';
}

// Raw vendor model - INTERNAL ONLY, never render to customers.
export function vendorModel(d) {
  if (!d) return '';
  if (d.type === 'bluetooth') return d.subType || 'BLE';
  if (d.type === 'digitalmatter') {
    const pid = d.metadata?.prodId;
    if (pid != null && DM_MODELS[pid]) return DM_MODELS[pid];
    return pid != null ? `DM prod ${pid}` : 'DigitalMatter';
  }
  return '';
}

// Customer-facing "model" = the Superb brand name, or '' when the device has no
// branded product (internal test devices stay anonymous - never leak a vendor name).
export function deviceModel(d) {
  return brandedProductForDevice(d)?.name || '';
}

// Customer-facing type label: the branded product name if known, else the neutral
// category ("Tracker (GPS)" / "BLE Sensor" / "BLE Beacon") with NO vendor model.
export function deviceTypeLabel(d) {
  const brand = brandedProductForDevice(d);
  return brand ? brand.name : deviceCategory(d);
}

// Thumbnails (cropped to subject, transparent PNG, sizes icon/small/normal under
// public/img/devices/). Only the rugged-tracker photo is brand-free; the BLE
// vendor photos have the model name printed on them, so we no longer surface
// them as thumbnails - the caller falls back to a Font Awesome icon instead.
// Oyster3 (87, 102) + GRIFFIN-AIR (118) share the clean rugged-tracker photo.
const DM_IMAGE_BY_PROD = { 87: 'oyster3', 102: 'oyster3', 118: 'oyster3' };

export function deviceImage(d, size = 'small') {
  if (!d) return null;
  // DM trackers keep the brand-free rugged photo bundled locally at three sizes.
  if (d.type === 'digitalmatter') {
    const pid = d.metadata?.prodId;
    const base = pid != null ? DM_IMAGE_BY_PROD[pid] : null;
    if (base) return `img/devices/${base}-${size}.png`;
  }
  // Any branded product (esp. BLE sensors/beacons like Engine+/Temp+) uses the
  // customer-safe manual artwork on the shared static origin - one image per brand,
  // scaled down via CSS. Non-branded BLE tags stay imageless (caller shows an icon).
  const brand = brandedProductForDevice(d);
  if (brand?.image) return deviceAssetUrl(brand.image);
  return null;
}
